import { makeAutoObservable, runInAction } from 'mobx';

import { notify } from 'components/common/Toast/toast.helpers';
import { ToastMessage } from 'components/common/Toast/toast.enums';
import { EmailNotificationEvent } from 'common/enums/emailNotifications.enums';

import { EmailNotifications } from './companyNotifications.types';
import { CompanyNotificationsApi } from './companyNotifications.api';
import { MAX_EMAIL_NOTIFICATIONS_COUNT } from './companyNotifications.constants';

function fillEmailNotificationsWithMaxCount(items: EmailNotifications[]): EmailNotifications[] {
  const result = [...items];

  while (result.length < MAX_EMAIL_NOTIFICATIONS_COUNT) {
    result.push({
      email: '',
      events: [],
      isCompanyEmail: false,
    });
  }

  return result.slice(0, MAX_EMAIL_NOTIFICATIONS_COUNT);
}

class CompanyNotificationsStore {
  private companyNotificationsApi: CompanyNotificationsApi;

  companyNotifications: EmailNotifications[] = [];

  isLoading = false;

  constructor() {
    this.companyNotificationsApi = new CompanyNotificationsApi();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async fetchNotifications() {
    this.isLoading = true;

    try {
      const response = await this.companyNotificationsApi.getAll();

      runInAction(() => {
        this.companyNotifications = fillEmailNotificationsWithMaxCount(response.items);
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async saveChanges(initialEmailNotifications: EmailNotifications[]) {
    this.isLoading = true;

    try {
      const deletions: Promise<void>[] = [];

      for (let i = 0; i < MAX_EMAIL_NOTIFICATIONS_COUNT; i++) {
        const oldItem = initialEmailNotifications[i];
        const newItem = this.companyNotifications[i];

        const oldHadEmail = oldItem?.email.trim() !== '';
        const newHasEmail = newItem?.email.trim() !== '';

        if (oldHadEmail && !newHasEmail && oldItem.id) {
          deletions.push(this.companyNotificationsApi.deleteById(oldItem.id));
        }
      }

      await Promise.all(deletions);

      const notificationsToUpsert = this.companyNotifications.filter((item) => item.email.trim() !== '');

      const response = await this.companyNotificationsApi.upsertMany(notificationsToUpsert);

      runInAction(() => {
        this.companyNotifications = fillEmailNotificationsWithMaxCount(response.items);
      });

      notify(ToastMessage.InformationSaved, 'success');
    } catch (error: any) {
      runInAction(() => {
        this.companyNotifications = initialEmailNotifications;
      });
      notify(error.message || 'Unexpected error', 'error');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  setEmail(rowIndex: number, newEmail: string) {
    this.companyNotifications[rowIndex].email = newEmail.trim();
  }

  toggleEvent(rowIndex: number, currentEvent: EmailNotificationEvent) {
    const item = this.companyNotifications[rowIndex];

    if (!item.email) {
      return;
    }

    if (item.events.includes(currentEvent)) {
      item.events = item.events.filter((event) => event !== currentEvent);
    } else {
      item.events.push(currentEvent);
    }
  }
}

export default CompanyNotificationsStore;
